import { JSX, useState, useEffect } from 'react'

import { Field, Text as ChakraText } from '@chakra-ui/react'


import * as detective from '@/lib/detective.js'


// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Editable from './editable.tsx'
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Datepicker from './datepicker.tsx'
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as File from './file.tsx'
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Combobox from './combobox.tsx'




export type Tprops<N extends string, T>
	= {
		label: string
		name : N
		value: T

		change(name: N, value: T): void

		invalid? : boolean

		helper?     : string
		placeholder?: string

	}


// eslint-disable-next-line @typescript-eslint/naming-convention
export function Root
<
	N extends string,
	T = string,

>
(
	props: Omit<Tprops<N, T>, 'change'>
			& {
				children?: React.ReactNode

			},

)
:	JSX.Element
{
	let [invalid, update_invalid] = useState(false)

	let required = detective.is_required_string(props.helper)

	useEffect(
		() =>
		{
			let a = props.invalid === true
			let b = required && detective.is_empty(props.value)

			update_invalid(a || b)

		},

		[props.invalid, props.value],

	)


	return (
		<Field.Root required={required} invalid={invalid}>
			<Legend name={props.label} required={required} />

			{props.children}

			<Field.ErrorText>{props.helper}</Field.ErrorText>

		</Field.Root>

	)

}


// eslint-disable-next-line @typescript-eslint/naming-convention
export function Legend
(
	props: {
		name     : string
		required?: boolean

	},

)
:	JSX.Element
{
	return (
		<Field.Label>
			<ChakraText as="span" fontSize="md" color="fg.subtle">{props.name}</ChakraText>

			{props.required === true && <Field.RequiredIndicator />}

		</Field.Label>

	)

}


// eslint-disable-next-line @typescript-eslint/naming-convention
export function Text<N extends string>
(
	props: Tprops<N, string>
		& {
			icon?: JSX.Element

		},

)

:	JSX.Element
{
	let on_change = (v: string): void =>
	{
		props.change(props.name, v)

	}

	return (
		<Root {...props}>
			<Editable.Text
				icon={props.icon}
				value={props.value}
				placeholder={props.placeholder}
				required={detective.is_required_string(props.helper)}
				change={on_change}
			/>

		</Root>

	)

}


// eslint-disable-next-line @typescript-eslint/naming-convention
export function RealNumber<N extends string>
(
	props: Tprops<N, number>
			& {
				icon?: JSX.Element
				min? : number
				max? : number

			},

)
:	JSX.Element
{
	let on_change = (v: number): void =>
	{
		props.change(props.name, v)

	}

	return (
		<Root {...props}>
			<Editable.RealNumber
				icon={props.icon}
				value={props.value}
				placeholder={props.placeholder}
				required={detective.is_required_string(props.helper)}
				min={props.min}
				max={props.max}
				change={on_change}
			/>

		</Root>

	)

}


// eslint-disable-next-line @typescript-eslint/naming-convention
export function Date
<N extends string> (props: Tprops<N, null | string>): JSX.Element
{
	let on_change = (v: null | string): void =>
	{
		props.change(props.name, v)

	}

	return (
		<Root {...props}>
			<Datepicker.Single value={props.value} change={on_change} />

		</Root>

	)

}


// eslint-disable-next-line @typescript-eslint/naming-convention
export function BetweenDate
<N extends string> (props: Tprops<N, detective.Range<string> >): JSX.Element
{
	let on_change = (v: detective.Range<string>): void =>
	{
		props.change(props.name, v)

	}

	return (
		<Root {...props}>
			<Datepicker.Between value={props.value} change={on_change} />

		</Root>

	)

}


// eslint-disable-next-line @typescript-eslint/naming-convention
export function RangeDate
<N extends string> (props: Tprops<N, detective.Range<string> >): JSX.Element
{
	let on_change = (v: detective.Range<string>): void =>
	{
		props.change(props.name, v)

	}

	return (
		<Root {...props}>
			<Datepicker.Range value={props.value} change={on_change} />

		</Root>

	)

}


// eslint-disable-next-line @typescript-eslint/naming-convention
export function Image
<
	N extends string,

>
(
	props: Omit<Tprops<N, string>, 'value'>
			& {
				thumbnail: string
				original?: string | ( () => Promise<string>)

				upload?(file: File): Promise<string>

			},

)
:	JSX.Element
{
	let on_change = (v: File): void =>
	{
		// eslint-disable-next-line @typescript-eslint/unbound-method
		if (detective.is_empty(props.upload) )
		{
			props.change(props.name, URL.createObjectURL(v) )

			return

		}

		props.upload(v)
			.then(props.change.bind(null, props.name) )
			.catch(
				() =>
				{
					//

				},

			)


	}

	return (
		<Root {...props} value={props.thumbnail}>
			<File.Image thumbnail={props.thumbnail} original={props.original} change={on_change} />

		</Root>

	)

}


// eslint-disable-next-line @typescript-eslint/naming-convention
export function Linker
<
	N extends string,
	T extends Combobox.TItem,

>
(
	props: Tprops<N, null | string>
			& {
				fit(value: string): Promise<string>
				search(keyword: string): Promise<T[]>

			},

)
:	JSX.Element
{
	let on_fit = (value: string): Promise<string> =>
	{
		return props.fit(value)

	}

	let on_search = (keyword: string): Promise<T[]> =>
	{
		return props.search(keyword)

	}

	let on_change = (value: string): void =>
	{
		props.change(props.name, value)

	}

	return (
		<Root {...props}>
			<Combobox.Default
				value={props.value}
				placeholder={props.placeholder}
				required={detective.is_required_string(props.helper)}
				fit={on_fit}
				search={on_search}
				change={on_change}
			/>

		</Root>

	)

}


export type LinkerItem = Combobox.TItem
